import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** One or two words, per the no-subtitles rule. Names the dialog. */
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * Bottom sheet for secondary content and text entry on the field app
 * (plan/smart-capture/wireframes/mobile-fit.html, Rules). Text entry opens
 * here so the on-screen keyboard lifts the sheet rather than pushing the
 * screen's primary action out of view.
 *
 * The sheet is an overlay, so it alone sizes by `dvh`: its height follows
 * the visible viewport as the keyboard and browser bars come and go, and its
 * own content scrolls when it runs past 85dvh. The page behind it keeps its
 * scrolling. The bottom safe-area inset is applied here, because while the
 * sheet is open it is the bottom-most surface on the screen.
 *
 * Escape, the backdrop and the close control all dismiss it. Focus moves into
 * the sheet on open and back to whatever held it on close.
 */
export function Sheet({ open, onClose, title, children, className }: SheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onCloseRef.current();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div aria-hidden="true" data-sheet-backdrop="" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative mx-auto flex max-h-[85dvh] w-full max-w-md flex-col rounded-t-card border border-b-0 border-border-strong bg-surface pb-[env(safe-area-inset-bottom)] text-surface-foreground shadow-2xl focus:outline-none sm:max-w-lg',
          className,
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 pl-4 pr-1 pt-1">
          <h2 id={titleId} className="text-base font-semibold">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex size-11 items-center justify-center rounded-control text-muted hover:text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 overflow-y-auto px-4 pb-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
